import { useRef, useState } from 'react';
import { documents } from '../../api/endpoints';
import type { Attachment, FileBlob } from '../../api/types';
import { FilePreviewModal } from '../../components/FilePreviewModal';
import { Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, label, shortId } from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

const DOC_TYPES = ['', 'ss', 'ds', 'refusal_letter', 'aip', 'sgr', 'other', 'unknown'];

const SOURCE_LABELS: Record<string, string> = {
  request: 'Загружен в заявку',
  general: 'Общий файл',
  inbound_raw: 'Входящее (исходник)',
  supplier_internal: 'Внутренний файл поставщика',
};

interface PreviewState {
  items: { blobId: string; name: string }[];
  index: number;
}

function attName(a: Attachment): string {
  return a.original_name || a.blob_original_name || a.filename || a.name || shortId(a.id);
}

function blobName(b: FileBlob): string {
  return b.original_name || b.filename || b.name || b.storage_key?.split('/').pop() || shortId(b.id);
}

export function FilesTab({ data }: { data: CardData }) {
  // Вложения могут быть привязаны к сообщениям/заявке — фильтруем по request, если поддерживается.
  const attachments = useAsync<Attachment[]>(
    () => documents.attachments({ request: data.request.id }).catch(() => [] as Attachment[]),
    [data.request.id],
  );
  // F06: показываем только blob'ы, привязанные к текущей заявке через Document/DocumentVersion.
  const blobs = useAsync<FileBlob[]>(
    () => documents.fileBlobs({ request: data.request.id }).catch(() => [] as FileBlob[]),
    [data.request.id],
  );

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [itemId, setItemId] = useState('');
  const [docType, setDocType] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const upload = async () => {
    const file = fileInput.current?.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null); setNotice('');
    try {
      const extra: Record<string, string> = {
        request_id: data.request.id,
        source_type: 'request',
      };
      if (itemId) extra.request_item_id = itemId;
      if (docType) extra.document_type = docType;
      await documents.upload(file, extra);
      setNotice('Файл загружен и привязан к заявке.');
      if (fileInput.current) fileInput.current.value = '';
      blobs.reload();
      attachments.reload();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  const openAttachmentPreview = (a: Attachment) => {
    if (!a.file_blob) return;
    const items = (attachments.data ?? [])
      .filter((x) => x.file_blob)
      .map((x) => ({ blobId: String(x.file_blob), name: attName(x) }));
    setPreview({ items, index: items.findIndex((x) => x.blobId === String(a.file_blob)) });
  };

  const openBlobPreview = (b: FileBlob) => {
    const items = (blobs.data ?? [])
      .map((x) => ({ blobId: String(x.id), name: blobName(x) }));
    setPreview({ items, index: items.findIndex((x) => x.blobId === String(b.id)) });
  };

  const attCols: Column<Attachment>[] = [
    {
      key: 'filename', title: 'Имя файла',
      render: (r) => (
        <button className="btn small" title={attName(r)}
                disabled={!r.file_blob}
                onClick={() => openAttachmentPreview(r)}>
          {attName(r)}
        </button>
      ),
    },
    { key: 'created_at', title: 'Добавлен', render: (r) => fmtDate(r.created_at) },
    {
      key: 'dl', title: '', render: (r) => r.file_blob ? (
        <a className="btn small" href={documents.downloadUrl(r.file_blob)}>Скачать</a>
      ) : null,
    },
  ];

  const blobCols: Column<FileBlob>[] = [
    {
      key: 'filename', title: 'Файл',
      render: (r) => (
        <button className="btn small" title={blobName(r)} onClick={() => openBlobPreview(r)}>
          {blobName(r)}
        </button>
      ),
    },
    { key: 'source_type', title: 'Источник', render: (r) => SOURCE_LABELS[r.source_type ?? ''] ?? r.source_type ?? '—' },
    { key: 'size', title: 'Размер', render: (r) => (r.size != null ? `${Math.round(r.size / 1024)} КБ` : '—') },
    { key: 'created_at', title: 'Загружен', render: (r) => fmtDate(r.created_at) },
    {
      key: 'dl', title: '', render: (r) => (
        <a className="btn small" href={documents.downloadUrl(r.id)}>Скачать</a>
      ),
    },
  ];

  return (
    <div>
      <Card title="Вложения заявки" actions={<button className="btn" onClick={() => { attachments.reload(); blobs.reload(); }}>Обновить</button>}>
        <ErrorBox error={attachments.error} />
        <DataTable columns={attCols} rows={attachments.data ?? []} empty="Вложений нет" />
      </Card>

      <Card title="Файлы заявки">
        <ErrorBox error={blobs.error} />
        {err ? <ErrorBox error={err} /> : null}
        {notice ? <div className="alert alert-info">{notice}</div> : null}
        {(blobs.data ?? []).length === 0
          ? <Empty text="Файлов нет или список недоступен для вашей роли" />
          : <DataTable columns={blobCols} rows={blobs.data ?? []} />}
        <div className="inline-form">
          <div className="form-row">
            <Field label="Файл"><input ref={fileInput} type="file" /></Field>
            <Field label="Позиция заявки">
              <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
                <option value="">Вся заявка</option>
                {data.items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
              </select>
            </Field>
            <Field label="Тип документа">
              <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                {DOC_TYPES.map((d) => <option key={d} value={d}>{d ? label(d) : '—'}</option>)}
              </select>
            </Field>
            <button className="btn primary" onClick={upload} disabled={busy}>
              {busy ? 'Загрузка…' : 'Загрузить файл'}
            </button>
          </div>
          <div className="field-hint">Файл будет прикреплён к выбранной позиции или ко всей заявке.</div>
        </div>
      </Card>
      {preview && (
        <FilePreviewModal items={preview.items} index={preview.index}
                          onClose={() => setPreview(null)}
                          onNavigate={(i) => setPreview((p) => p && { ...p, index: i })} />
      )}
    </div>
  );
}
